import { useState } from "react";
import { api } from "@/lib/api";
import SimpleMD from "@/components/app/SimpleMD";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, X, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * AISummaryDialog — triggers /api/ai/summarise on click, streams the result into a modal.
 *
 * Props:
 *   targetType: "chat" | "wiki_page"
 *   projectId:  string
 *   targetId:   string|null   (page_id for wiki, null for chat)
 *   hours:      number        (chat window — default 72)
 *   label:      string        (button label, e.g. "Catch me up" or "AI summary")
 *   testId:     string
 */
const AISummaryDialog = ({ targetType, projectId, targetId = null, hours = 72, label = "AI summary", testId = "ai-summary-btn" }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState("");
  const [copied, setCopied] = useState(false);

  const run = async () => {
    setOpen(true);
    setLoading(true);
    setSummary("");
    try {
      const { data } = await api.post("/ai/summarise", {
        target_type: targetType,
        project_id: projectId,
        target_id: targetId,
        hours,
      });
      setSummary(data.summary);
    } catch (e) {
      const detail = e?.response?.data?.detail || "AI summary failed";
      setSummary(`> **Couldn't generate summary**\n>\n> ${detail}`);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(summary).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1500); },
      () => toast.error("Copy failed")
    );
  };

  return (
    <>
      <Button
        onClick={run}
        variant="outline"
        className="rounded-none border-[#2A7BFF] text-[#2A7BFF] hover:bg-[#2A7BFF] hover:text-white"
        data-testid={testId}
      >
        <Sparkles className="w-4 h-4 mr-1" /> {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border-gray-900 sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="ai-summary-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#2A7BFF]" strokeWidth={1.7} />
              {targetType === "chat" ? `Chat summary — last ${hours}h` : "Page summary"}
            </DialogTitle>
          </DialogHeader>

          <div className="min-h-[200px]" data-testid="ai-summary-body">
            {loading ? (
              <div className="py-8 text-center">
                <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                  <Sparkles className="w-4 h-4 text-[#2A7BFF] animate-pulse" strokeWidth={1.7} />
                  Gemini is reading the thread…
                </div>
              </div>
            ) : summary ? (
              <SimpleMD text={summary} />
            ) : null}
          </div>

          {summary && !loading && (
            <div className="flex justify-between items-center pt-3 border-t border-[#1F2937]">
              <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 font-mono">
                Generated by Gemini
              </div>
              <Button
                onClick={copy}
                variant="ghost"
                size="sm"
                className="rounded-none text-xs"
                data-testid="ai-summary-copy"
              >
                {copied ? <><Check className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AISummaryDialog;
